import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Calendar, CreditCard, DollarSign, FileText, X } from "lucide-react";
import { INVOICE_STATUSES, INVOICE_STATUS_LABELS } from "@/constants/invoices";
import { Select } from "@/components/ui/Select";
import useAuthStore from "@/context/auth/useAuthStore";
import { useApiKeys } from "@/hooks/api-keys";
import { formatCurrency, formatUsageValue } from "@/utils/formatters";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
export default function InvoiceDetailModal({ invoice, isOpen, onClose, isSuperAdmin, onStatusChange, isUpdatingStatus, onDownloadInvoice, isDownloading }) {
    const { productCode } = useAuthStore();
    const { data: apiKeysData } = useApiKeys(productCode || "");
    if (!isOpen || !invoice) return null;
    // Create a map of API key IDs to names
    const apiKeyNameMap = new Map();
    if (apiKeysData?.items) {
        apiKeysData.items.forEach((key)=>{
            apiKeyNameMap.set(key.id, key.name || "Unnamed Key");
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-50 overflow-y-auto",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity",
                    onClick: onClose
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between mb-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center space-x-4",
                                            children: [
                                                /*#__PURE__*/ _jsx(FileText, {
                                                    className: "h-6 w-6 text-navy"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("h3", {
                                                            className: "text-lg font-semibold text-gray-900",
                                                            children: [
                                                                "Invoice ",
                                                                invoice.invoiceId
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-gray-500",
                                                            children: invoice.productCode
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center space-x-4",
                                            children: [
                                                /*#__PURE__*/ _jsx(InvoiceStatusBadge, {
                                                    status: invoice.status
                                                }),
                                                isSuperAdmin && onStatusChange && /*#__PURE__*/ _jsxs(Select, {
                                                    value: "",
                                                    disabled: isUpdatingStatus,
                                                    onChange: (e)=>{
                                                        if (e.target.value) {
                                                            onStatusChange(invoice, e.target.value);
                                                        }
                                                    },
                                                    className: "w-40",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("option", {
                                                            value: "",
                                                            children: "Change status"
                                                        }),
                                                        Object.values(INVOICE_STATUSES).filter((s)=>s !== invoice.status).map((s)=>/*#__PURE__*/ _jsx("option", {
                                                                value: s,
                                                                children: INVOICE_STATUS_LABELS[s]
                                                            }, s))
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("button", {
                                                    onClick: onClose,
                                                    className: "text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150",
                                                    children: /*#__PURE__*/ _jsx(X, {
                                                        className: "h-6 w-6"
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-1 md:grid-cols-3 gap-6 mb-6",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "bg-gray-50 p-4 rounded-lg",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center space-x-2 mb-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Calendar, {
                                                            className: "h-4 w-4 text-gray-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm font-medium text-gray-700",
                                                            children: "Period"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("p", {
                                                    className: "text-sm text-gray-900",
                                                    children: [
                                                        new Date(invoice.periodStart).toLocaleDateString(),
                                                        " -",
                                                        " ",
                                                        new Date(invoice.periodEnd).toLocaleDateString()
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "bg-gray-50 p-4 rounded-lg",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center space-x-2 mb-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(FileText, {
                                                            className: "h-4 w-4 text-gray-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm font-medium text-gray-700",
                                                            children: "Issued"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-gray-900",
                                                    children: new Date(invoice.issuedAt).toLocaleDateString()
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "bg-gray-50 p-4 rounded-lg",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center space-x-2 mb-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(CreditCard, {
                                                            className: "h-4 w-4 text-gray-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm font-medium text-gray-700",
                                                            children: "Due Date"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm text-gray-900",
                                                    children: new Date(invoice.dueAt).toLocaleDateString()
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mb-6",
                                    children: [
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-md font-semibold text-gray-900 mb-3",
                                            children: "Usage Summary"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "bg-blue-50 p-4 rounded-lg",
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-sm font-medium text-gray-700",
                                                        children: "Total Usage"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-sm font-semibold text-gray-900",
                                                        children: formatUsageValue(invoice.totalUsageValue, invoice.totalUsageUnit)
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mb-6",
                                    children: [
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-md font-semibold text-gray-900 mb-3",
                                            children: "Usage by API Key"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "overflow-x-auto",
                                            children: /*#__PURE__*/ _jsxs("table", {
                                                className: "min-w-full divide-y divide-gray-200",
                                                children: [
                                                    /*#__PURE__*/ _jsx("thead", {
                                                        className: "bg-gray-50",
                                                        children: /*#__PURE__*/ _jsxs("tr", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx("th", {
                                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                                    children: "API Name"
                                                                }),
                                                                /*#__PURE__*/ _jsx("th", {
                                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                                    children: "Usage"
                                                                }),
                                                                /*#__PURE__*/ _jsx("th", {
                                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                                    children: "Records"
                                                                })
                                                            ]
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("tbody", {
                                                        className: "bg-white divide-y divide-gray-200",
                                                        children: invoice.lineItems.map((item)=>{
                                                            const apiKeyName = apiKeyNameMap.get(item.apiKeyId) ?? `Deleted (${item.apiKeyId.substring(0, 8)}...)`;
                                                            return /*#__PURE__*/ _jsxs("tr", {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("td", {
                                                                        className: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900",
                                                                        children: apiKeyName
                                                                    }),
                                                                    /*#__PURE__*/ _jsx("td", {
                                                                        className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                                                        children: formatUsageValue(item.usageValue, item.usageUnit)
                                                                    }),
                                                                    /*#__PURE__*/ _jsx("td", {
                                                                        className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                                                        children: item.metadata?.recordCount?.toLocaleString() || "-"
                                                                    })
                                                                ]
                                                            }, item.lineId);
                                                        })
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "border-t pt-6",
                                    children: [
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-md font-semibold text-gray-900 mb-4",
                                            children: "Financial Summary"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-3",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex justify-between items-center",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm text-gray-600",
                                                            children: "Subtotal"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm font-medium text-gray-900",
                                                            children: formatCurrency(invoice.subtotal, invoice.currency)
                                                        })
                                                    ]
                                                }),
                                                invoice.credits > 0 && /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex justify-between items-center",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm text-gray-600",
                                                            children: "Credits"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "text-sm font-medium text-green-600",
                                                            children: [
                                                                "-",
                                                                formatCurrency(invoice.credits, invoice.currency)
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("hr", {
                                                    className: "my-2"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex justify-between items-center",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-base font-semibold text-gray-900",
                                                            children: "Amount Due"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-base font-bold text-gray-900",
                                                            children: formatCurrency(invoice.amountDue, invoice.currency)
                                                        })
                                                    ]
                                                }),
                                                invoice.amountPaid > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex justify-between items-center",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-sm text-gray-600",
                                                                    children: "Amount Paid"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-sm font-medium text-green-600",
                                                                    children: formatCurrency(invoice.amountPaid, invoice.currency)
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex justify-between items-center",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-base font-semibold text-gray-900",
                                                                    children: "Balance"
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-base font-bold text-gray-900",
                                                                    children: formatCurrency(invoice.balance, invoice.currency)
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                invoice.notes && /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-6 pt-6 border-t",
                                    children: [
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-md font-semibold text-gray-900 mb-2",
                                            children: "Notes"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-600",
                                            children: invoice.notes
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    className: "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-navy text-base font-medium text-white hover:bg-navy/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy sm:ml-3 sm:w-auto sm:text-sm",
                                    onClick: onClose,
                                    children: "Close"
                                }),
                                onDownloadInvoice && /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    disabled: isDownloading,
                                    className: "mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50",
                                    onClick: ()=>onDownloadInvoice(invoice),
                                    children: [
                                        /*#__PURE__*/ _jsx(DollarSign, {
                                            className: "h-4 w-4 mr-2"
                                        }),
                                        isDownloading ? "Downloading..." : "Download PDF"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
